import React from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

import FilterAutocomplete from './FilterAutocomplete';

FilterDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  manufacturers: PropTypes.array.isRequired,
  subcategories: PropTypes.array.isRequired,
  allVendors: PropTypes.array.isRequired,
};

export default function FilterDrawer({ open, setOpen, filter, setFilter, manufacturers, subcategories, allVendors }) {
  const accordionStyle = {
    borderBottom: '1px solid #e0e0e0',
    '&:before': { display: 'none' },
    '& .MuiAccordionSummary-root': { minHeight: 56, px: 0 },
    '& .MuiAccordionSummary-content': { margin: 0, alignItems: 'center' },
    '& .MuiTypography-subtitle2': { fontSize: '0.95rem', fontWeight: 500 },
    '& .MuiAccordionDetails-root': { pb: 1.5 },
  };

  const filterItems = [
    { key: 'description', label: 'Description' },
    { key: 'sku', label: 'SKU' },
    { key: 'subcategory', label: 'Subcategory' },
    { key: 'price', label: 'Price' },
    { key: 'productId', label: 'PS ID' },
    { key: 'manufacturer', label: 'Manufacturer' },
    { key: 'manufacturerId', label: 'Manufacturer ID' },
    { key: 'uom', label: 'UOM' },
    { key: 'vendor', label: 'Vendor' },
  ];

  return (
    <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
      <Box sx={{ width: 330, p: 2, pt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="600">
            All Filters
          </Typography>
          <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {filterItems.map((filterItem) => {
          switch (filterItem.key) {
            case 'description':
              return (
                <Accordion key={filterItem.key} disableGutters elevation={0} sx={accordionStyle}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{filterItem.label}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      variant="outlined"
                      size="small"
                      autoComplete="off"
                      fullWidth
                      value={filter.description || ''}
                      onChange={(e) => setFilter((f) => ({ ...f, description: e.target.value }))}
                    />
                  </AccordionDetails>
                </Accordion>
              );

            case 'sku':
              return (
                <Accordion key={filterItem.key} disableGutters elevation={0} sx={accordionStyle}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{filterItem.label}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      variant="outlined"
                      size="small"
                      autoComplete="off"
                      fullWidth
                      value={filter.sku || ''}
                      onChange={(e) => setFilter((f) => ({ ...f, sku: e.target.value }))}
                    />
                  </AccordionDetails>
                </Accordion>
              );

            case 'price':
              return (
                <Accordion key={filterItem.key} disableGutters elevation={0} sx={accordionStyle}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{filterItem.label}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Min"
                        type="number"
                        size="small"
                        autoComplete="off"
                        value={filter.minPrice || ''}
                        onChange={(e) => setFilter((f) => ({ ...f, minPrice: e.target.value }))}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={{ maxWidth: 90 }}
                      />
                      <TextField
                        label="Max"
                        type="number"
                        size="small"
                        autoComplete="off"
                        value={filter.maxPrice || ''}
                        onChange={(e) => setFilter((f) => ({ ...f, maxPrice: e.target.value }))}
                        sx={{ maxWidth: 90 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );

            case 'manufacturer':
              return (
                <Accordion key={filterItem.key} disableGutters elevation={0} sx={accordionStyle}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{filterItem.label}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FilterAutocomplete
                      options={manufacturers}
                      value={filter.manufacturers || []}
                      onChange={(newValue) => setFilter((f) => ({ ...f, manufacturers: newValue }))}
                    />
                  </AccordionDetails>
                </Accordion>
              );

            case 'subcategory':
              return (
                <Accordion key={filterItem.key} disableGutters elevation={0} sx={accordionStyle}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{filterItem.label}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FilterAutocomplete
                      options={subcategories}
                      value={filter.subcategories || []}
                      onChange={(newValue) => setFilter((f) => ({ ...f, subcategories: newValue }))}
                    />
                  </AccordionDetails>
                </Accordion>
              );

            case 'vendor':
              return (
                <Accordion key={filterItem.key} disableGutters elevation={0} sx={accordionStyle}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{filterItem.label}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FilterAutocomplete
                      options={allVendors}
                      value={filter.vendor || []}
                      onChange={(newValue) => setFilter((f) => ({ ...f, vendor: newValue }))}
                    />
                  </AccordionDetails>
                </Accordion>
              );

            default:
              return (
                <Accordion key={filterItem.key} disableGutters elevation={0} sx={accordionStyle}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{filterItem.label}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      variant="outlined"
                      size="small"
                      autoComplete="off"
                      fullWidth
                      value={filter[filterItem.key] || ''}
                      onChange={(e) => setFilter((f) => ({ ...f, [filterItem.key]: e.target.value }))}
                    />
                  </AccordionDetails>
                </Accordion>
              );
          }
        })}
      </Box>
    </Drawer>
  );
}
